import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Apply configurator preview images (_pcs_image) onto cart line thumbnails.
 * @param {ParentNode} [root]
 */
async function applyPcsCartImages(root) {
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    if (!cart?.items?.length) return;

    const scope = root || document;
    for (const item of cart.items) {
      const pcs = item.properties?._pcs_image;
      if (!pcs) continue;
      const url = pcs.startsWith('//') ? `https:${pcs}` : pcs;
      const row =
        scope.querySelector(`[data-key="${CSS.escape(item.key)}"]`) ||
        document.querySelector(`[data-key="${CSS.escape(item.key)}"]`);
      if (!row) continue;

      row.querySelectorAll('.cart-items__media-image, .cart-item__image img, img').forEach((img) => {
        if (img.getAttribute('src') !== url) {
          img.setAttribute('src', url);
          img.removeAttribute('srcset');
        }
      });
    }
  } catch (e) {
    // ignore
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Catch plain cart:update from the configurator as well
    document.addEventListener('cart:update', this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartAdd);
  }

  #handleCartAdd = async () => {
    // Allow section morph to finish, then force correct thumbnails
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 50)));
    await applyPcsCartImages(this);
    await new Promise((r) => setTimeout(r, 300));
    await applyPcsCartImages(this);

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    applyPcsCartImages(this);
    this.showDialog();
    // Re-apply after dialog content paints / morphs
    setTimeout(() => applyPcsCartImages(this), 100);
    setTimeout(() => applyPcsCartImages(this), 400);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
